import React, { useEffect, useMemo, useState } from "react";
import { FaCameraRetro, FaImages, FaTimes, FaExpand } from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";
import SectionTitle from "./SectionTitle";

import { getAllGalleries } from "../../../services/gallerieServices";
import { resolveMediaUrl, truncateText } from "./homeUtils";

const LastGallerie = () => {
  const [galleries, setGalleries] = useState([]);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchGalleries = async () => {
      try {
        setLoading(true);

        const allGalleries = await getAllGalleries();

        const activeGalleries = Array.isArray(allGalleries)
          ? allGalleries
              .filter((gallery) => gallery.is_active)
              .sort((a, b) => {
                const dateA = new Date(
                  a.created_at || a.updated_at || 0,
                ).getTime();
                const dateB = new Date(
                  b.created_at || b.updated_at || 0,
                ).getTime();
                return dateB - dateA;
              })
              .slice(0, 3)
          : [];

        if (mounted) setGalleries(activeGalleries);
      } catch (error) {
        console.error("Erreur lors de la récupération des galeries:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchGalleries();

    return () => {
      mounted = false;
    };
  }, []);

  const selectedPhotos = useMemo(() => {
    if (!selectedGallery?.photos) return [];

    return selectedGallery.photos.filter((photo) => {
      if (typeof photo.is_active === "boolean") return photo.is_active;
      return true;
    });
  }, [selectedGallery]);

  const openGalleryModal = (gallery) => {
    setSelectedGallery(gallery);
    setSelectedImage(null);
  };

  const closeGalleryModal = () => {
    setSelectedGallery(null);
    setSelectedImage(null);
  };

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  return (
    <section className="bg-slate-50 py-16 dark:bg-slate-950 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow="Photos"
          title="Les derniers "
          highlight="albums photos"
          subtitle="Découvrez en images les activités, événements et moments forts des associations de la commune."
        />

        {loading ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center text-slate-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300">
            Chargement des albums...
          </div>
        ) : galleries.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300">
            Aucun album photo disponible pour le moment.
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-3">
            {galleries.map((gallery, index) => {
              const photos = Array.isArray(gallery.photos)
                ? gallery.photos.filter((photo) =>
                    typeof photo.is_active === "boolean"
                      ? photo.is_active
                      : true,
                  )
                : [];

              const cover = photos[0]?.photo_url;
              const coverUrl = resolveMediaUrl(cover, "/img/cours_judo.jpg");

              return (
                <Reveal key={gallery.id} delay={index * 0.05}>
                  <article
                    role="button"
                    tabIndex={0}
                    onClick={() => openGalleryModal(gallery)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") openGalleryModal(gallery);
                    }}
                    className="group h-full cursor-pointer overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-2xl dark:border-white/10 dark:bg-slate-900"
                  >
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={coverUrl}
                        alt={gallery.titre}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
                      />

                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />

                      <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-primary shadow">
                        <FaImages />
                        {photos.length} photo{photos.length > 1 ? "s" : ""}
                      </div>

                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-2xl font-bold text-white">
                          {gallery.titre}
                        </h3>
                      </div>
                    </div>

                    <div className="p-5">
                      <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                        {truncateText(
                          gallery.description ||
                            "Voir les photos de cet album.",
                          140,
                        )}
                      </p>

                      <div className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-primary">
                        <FaCameraRetro />
                        Ouvrir l’album
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>

      {selectedGallery && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm"
          onClick={closeGalleryModal}
        >
          <div
            className="relative max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-2xl dark:bg-slate-900"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-white/10">
              <div>
                <p className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">
                  <FaImages />
                  Album photo
                </p>

                <h2 className="mt-3 text-2xl font-bold text-slate-950 dark:text-white">
                  {selectedGallery.titre}
                </h2>

                {selectedGallery.description && (
                  <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500 dark:text-slate-300">
                    {selectedGallery.description}
                  </p>
                )}
              </div>

              <button
                type="button"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
                onClick={closeGalleryModal}
                aria-label="Fermer"
              >
                <FaTimes />
              </button>
            </div>

            <div className="max-h-[68vh] overflow-y-auto p-5">
              {selectedPhotos.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-slate-300 p-10 text-center text-slate-500 dark:border-white/10 dark:text-slate-300">
                  Aucune photo dans cet album.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {selectedPhotos.map((photo) => {
                    const imageUrl = resolveMediaUrl(photo.photo_url);

                    return (
                      <button
                        type="button"
                        key={photo.id}
                        onClick={() => openImageModal(imageUrl)}
                        className="group relative h-36 overflow-hidden rounded-2xl bg-slate-100 shadow-sm sm:h-44 dark:bg-slate-950"
                      >
                        <img
                          src={imageUrl}
                          alt={photo.titre || selectedGallery.titre}
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
                        />

                        <div className="absolute inset-0 flex items-center justify-center bg-slate-950/0 opacity-0 transition group-hover:bg-slate-950/45 group-hover:opacity-100">
                          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-primary shadow">
                            <FaExpand />
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedImage && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/90 p-4 backdrop-blur-sm"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-h-[90vh] max-w-6xl">
            <img
              src={selectedImage}
              alt="Agrandissement"
              className="max-h-[90vh] rounded-3xl object-contain shadow-2xl"
            />

            <button
              type="button"
              className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-950 shadow"
              onClick={() => setSelectedImage(null)}
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default LastGallerie;
